import React from 'react'
import { Box, Card, Chip, LinearProgress, Tooltip } from '@mui/material'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
import WTypography from '../../../Components/whiteTypography'
import { scoreToColor, scoreToGrade } from '../utils/scoringEngine'

/**
 * ScorePatrimonial - Hero component
 * Carte principale : score global /100, grade A→F, trend, percentile,
 * et décomposition par dimension pondérée.
 */
function ScorePatrimonial({ score, dimensions, userFirstName }) {
  const { t } = useTranslation('layout')
  const grade = scoreToGrade(score.global)
  const color = scoreToColor(score.global)
  const trendValue = parseInt(score.trend, 10)
  const TrendIcon = trendValue >= 0 ? TrendingUpIcon : TrendingDownIcon

  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #0f1d3a 0%, #1a2f5e 100%)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 3,
        p: 4,
        color: 'white',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <WTypography variant="overline" sx={{ color: '#a0b4d6 !important', letterSpacing: 1.5 }}>
            {t('conseillerPatrimonial.score.overline')}
          </WTypography>
          <WTypography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            {t('conseillerPatrimonial.score.title', { name: userFirstName })}
          </WTypography>
          <WTypography variant="body2" sx={{ color: '#c1d0eb !important', mt: 1 }}>
            {t('conseillerPatrimonial.score.subtitle', { percentile: score.percentile })}
          </WTypography>
        </Box>
        <Chip
          icon={<TrendIcon sx={{ color: 'inherit !important' }} />}
          label={t('conseillerPatrimonial.score.trendLabel', {
            sign: trendValue >= 0 ? '+' : '',
            value: trendValue,
          })}
          sx={{
            bgcolor: trendValue >= 0 ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
            color: trendValue >= 0 ? '#4ade80' : '#f87171',
            fontWeight: 600,
          }}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={4} flexWrap="wrap" mb={4}>
        <ScoreCircle value={score.global} grade={grade} color={color} t={t} />
        <Box flex={1} minWidth={260}>
          <WTypography variant="h6" sx={{ fontWeight: 600 }}>
            {t('conseillerPatrimonial.score.diagnosticTitle')}
          </WTypography>
          <WTypography variant="body1" sx={{ color: '#dbe5f7 !important', mt: 1, lineHeight: 1.6 }}>
            {t('conseillerPatrimonial.score.diagnosticBody')}
          </WTypography>
        </Box>
      </Box>

      <WTypography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#dbe5f7 !important' }}>
        {t('conseillerPatrimonial.score.dimensionsTitle')}
      </WTypography>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }}
        gap={2}
      >
        {dimensions.map((d) => (
          <DimensionRow key={d.key} dim={d} t={t} />
        ))}
      </Box>
    </Card>
  )
}

function ScoreCircle({ value, grade, color, t }) {
  const size = 180
  const stroke = 12
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference * (1 - value / 100)

  return (
    <Box position="relative" width={size} height={size}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <defs>
          <linearGradient id="scoreGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity={0.6} />
            <stop offset="100%" stopColor={color} stopOpacity={1} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.08)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#scoreGrad)"
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.2s ease-out' }}
        />
      </svg>
      <Box
        position="absolute"
        top={0}
        left={0}
        width={size}
        height={size}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <WTypography sx={{ fontSize: 52, fontWeight: 800, lineHeight: 1 }}>
          {value}
        </WTypography>
        <WTypography sx={{ fontSize: 14, color: '#a0b4d6 !important' }}>
          {t('conseillerPatrimonial.score.outOf100')}
        </WTypography>
        <Chip
          label={t('conseillerPatrimonial.score.gradeLabel', { grade })}
          size="small"
          sx={{
            mt: 0.5,
            bgcolor: color,
            color: '#0a1429',
            fontWeight: 700,
            fontSize: 11,
          }}
        />
      </Box>
    </Box>
  )
}

function DimensionRow({ dim, t }) {
  const color = scoreToColor(dim.score)
  const statusColor =
    { critical: '#f87171', warning: '#fbbf24', good: '#4ade80' }[dim.status] || '#94a3b8'

  return (
    <Tooltip title={dim.detail} arrow placement="top">
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.06)',
          cursor: 'help',
          transition: 'all .2s',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.07)',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <WTypography variant="body2" sx={{ fontWeight: 600 }}>
            {dim.label}
          </WTypography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <WTypography variant="body2" sx={{ color: `${color} !important`, fontWeight: 700 }}>
              {dim.score}
            </WTypography>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#a0b4d6' }} />
          </Box>
        </Box>
        <LinearProgress
          variant="determinate"
          value={dim.score}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'rgba(255,255,255,0.08)',
            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
          }}
        />
        <Box display="flex" alignItems="center" gap={0.5} mt={1}>
          <Box width={6} height={6} borderRadius="50%" bgcolor={statusColor} />
          <WTypography variant="caption" sx={{ color: '#a0b4d6 !important' }}>
            {t('conseillerPatrimonial.score.weightLabel', { weight: Math.round(dim.weight * 100) })}
          </WTypography>
        </Box>
      </Box>
    </Tooltip>
  )
}

export default ScorePatrimonial
